import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { 
  FileText, Users, Receipt, Plus, Pencil, Trash2, Eye, 
  Download, ArrowRight, Loader2, Search, Filter,
  CheckCircle2, Clock, XCircle, Send, Euro, TrendingUp,
  Check, ChevronsUpDown, Package
} from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import axios from 'axios';
import { exportFacturePDF } from '../utils/pdfExport';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Composant Badge Statut
const StatutBadge = ({ statut, type }) => {
  const config = {
    brouillon: { label: 'Brouillon', color: 'bg-slate-100 text-slate-700' },
    envoye: { label: 'Envoyé', color: 'bg-blue-100 text-blue-700' },
    envoyee: { label: 'Envoyée', color: 'bg-blue-100 text-blue-700' },
    accepte: { label: 'Accepté', color: 'bg-emerald-100 text-emerald-700' },
    refuse: { label: 'Refusé', color: 'bg-red-100 text-red-700' },
    expire: { label: 'Expiré', color: 'bg-amber-100 text-amber-700' },
    converti: { label: 'Converti', color: 'bg-purple-100 text-purple-700' },
    payee: { label: 'Payée', color: 'bg-emerald-100 text-emerald-700' },
    partiellement_payee: { label: 'Partiel', color: 'bg-amber-100 text-amber-700' },
    annulee: { label: 'Annulée', color: 'bg-red-100 text-red-700' }
  };
  
  const cfg = config[statut] || { label: statut, color: 'bg-slate-100 text-slate-700' };
  
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${cfg.color}`}>
      {cfg.label}
    </span>
  );
};

// Composant Ligne de document (devis/facture) avec sélection de prestation
const LigneForm = ({ ligne, index, onChange, onRemove, showTva, prestations }) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  
  const handleChange = (field, value) => {
    const newLigne = { ...ligne, [field]: value };
    onChange(index, newLigne);
  };
  
  const handleSelectPrestation = (prestation) => {
    const newLigne = {
      ...ligne,
      prestation_id: prestation.id,
      description: prestation.nom,
      prix_unitaire: prestation.prix_vente_ht,
      taux_tva: prestation.taux_tva || 0
    };
    onChange(index, newLigne);
    setPopoverOpen(false);
  };
  
  const montantHT = (ligne.quantite || 0) * (ligne.prix_unitaire || 0);
  
  return (
    <div className="grid grid-cols-12 gap-2 items-center p-2 bg-slate-50 rounded-lg">
      <div className="col-span-4">
        <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              className={cn(
                "w-full justify-between font-normal text-left h-9",
                !ligne.description && "text-muted-foreground"
              )}
            >
              <span className="truncate text-sm">
                {ligne.description || "Sélectionner..."}
              </span>
              <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[320px] p-0" align="start">
            <Command>
              <CommandInput placeholder="Rechercher..." />
              <CommandList>
                <CommandEmpty>
                  <p className="text-sm text-muted-foreground py-2">Aucune prestation</p>
                </CommandEmpty>
                {prestations && prestations.length > 0 && (
                  <CommandGroup heading="Catalogue">
                    {prestations.map((p) => (
                      <CommandItem
                        key={p.id}
                        value={p.nom}
                        onSelect={() => handleSelectPrestation(p)}
                        className="cursor-pointer"
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            ligne.prestation_id === p.id ? "opacity-100" : "opacity-0"
                          )}
                        />
                        <div className="flex-1 flex items-center justify-between">
                          <span className="text-sm">{p.nom}</span>
                          <span className="text-xs text-muted-foreground">
                            {p.prix_vente_ht?.toFixed(2)}€
                          </span>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
            <div className="border-t p-2">
              <Input
                placeholder="Ou saisir manuellement..."
                value={ligne.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
                className="text-sm h-8"
              />
            </div>
          </PopoverContent>
        </Popover>
      </div>
      <div className="col-span-2">
        <Input
          type="number"
          placeholder="Qté"
          value={ligne.quantite || ''}
          onChange={(e) => handleChange('quantite', parseFloat(e.target.value) || 0)}
          min="0"
          step="0.5"
          data-testid={`ligne-quantite-${index}`}
        />
      </div>
      <div className="col-span-2">
        <Input
          type="number"
          placeholder="Prix unit."
          value={ligne.prix_unitaire || ''}
          onChange={(e) => handleChange('prix_unitaire', parseFloat(e.target.value) || 0)}
          min="0"
          step="0.01"
          data-testid={`ligne-prix-${index}`}
        />
      </div>
      {showTva && (
        <div className="col-span-1">
          <Select value={String(ligne.taux_tva || 0)} onValueChange={(v) => handleChange('taux_tva', parseFloat(v))}>
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="0">0%</SelectItem>
              <SelectItem value="5.5">5.5%</SelectItem>
              <SelectItem value="10">10%</SelectItem>
              <SelectItem value="20">20%</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
      <div className={`text-right font-mono font-medium ${showTva ? 'col-span-2' : 'col-span-3'}`}>
        {montantHT.toFixed(2)} €
      </div>
      <div className="col-span-1 text-right">
        <Button variant="ghost" size="sm" onClick={() => onRemove(index)} data-testid={`remove-ligne-${index}`}>
          <Trash2 className="h-4 w-4 text-red-500" />
        </Button>
      </div>
    </div>
  );
};

// Composant KPI Card
const KPICard = ({ title, value, subtitle, icon: Icon, color }) => {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${color}`}>
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <p className="text-xs text-slate-500">{title}</p>
            <p className="text-xl font-bold text-slate-900" data-testid={`kpi-${title.toLowerCase().replace(/\s/g, '-')}`}>{value}</p>
            {subtitle && <p className="text-xs text-slate-400">{subtitle}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const FacturationPage = () => {
  const { token } = useAuth();
  const { formatMontant } = useSettings();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('factures');
  
  // Data states
  const [factures, setFactures] = useState([]);
  const [devis, setDevis] = useState([]);
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState(null);
  const [facturationSettings, setFacturationSettings] = useState(null);
  const [prestations, setPrestations] = useState([]);
  
  // Modal states
  const [showClientModal, setShowClientModal] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  
  // Form states
  const [editingClient, setEditingClient] = useState(null);
  const [editingDocument, setEditingDocument] = useState(null);
  const [documentType, setDocumentType] = useState('facture');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [convertDevis, setConvertDevis] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Client form
  const [clientForm, setClientForm] = useState({
    nom: '', email: '', telephone: '', adresse: '',
    code_postal: '', ville: '', pays: 'France', siret: '', notes: ''
  });
  
  // Document form
  const [documentForm, setDocumentForm] = useState({
    client_id: '',
    lignes: [{ description: '', quantite: 1, prix_unitaire: 0, taux_tva: 0 }],
    conditions_paiement: 'Paiement à 30 jours',
    validite_jours: 30,
    notes: ''
  });

  // Fetch data
  const fetchClients = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/facturation/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data.clients || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  }, [token]);
  
  const fetchFactures = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/facturation/factures`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFactures(response.data.factures || []);
    } catch (error) {
      console.error('Error fetching factures:', error);
    }
  }, [token]);
  
  const fetchDevis = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/facturation/devis`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDevis(response.data.devis || []);
    } catch (error) {
      console.error('Error fetching devis:', error);
    }
  }, [token]);
  
  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/facturation/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [token]);
  
  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/facturation/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFacturationSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  }, [token]);

  const fetchPrestations = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/prestations?actif=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPrestations(response.data.prestations || []);
    } catch (error) {
      console.error('Error fetching prestations:', error);
    }
  }, [token]);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([fetchClients(), fetchFactures(), fetchDevis(), fetchStats(), fetchSettings(), fetchPrestations()]);
      setLoading(false);
    };
    loadAll();
  }, [fetchClients, fetchFactures, fetchDevis, fetchStats, fetchSettings, fetchPrestations]);

  // Client handlers
  const openNewClient = () => {
    setEditingClient(null);
    setClientForm({
      nom: '', email: '', telephone: '', adresse: '',
      code_postal: '', ville: '', pays: 'France', siret: '', notes: ''
    });
    setShowClientModal(true);
  };
  
  const openEditClient = (client) => {
    setEditingClient(client);
    setClientForm({
      nom: client.nom || '',
      email: client.email || '',
      telephone: client.telephone || '',
      adresse: client.adresse || '',
      code_postal: client.code_postal || '',
      ville: client.ville || '',
      pays: client.pays || 'France',
      siret: client.siret || '',
      notes: client.notes || ''
    });
    setShowClientModal(true);
  };
  
  const handleSaveClient = async () => {
    if (!clientForm.nom.trim()) {
      toast.error('Le nom du client est obligatoire');
      return;
    }
    
    setSaving(true);
    try {
      if (editingClient) {
        await axios.put(
          `${API_URL}/api/facturation/clients/${editingClient.id}`,
          clientForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Client mis à jour');
      } else {
        await axios.post(
          `${API_URL}/api/facturation/clients`,
          clientForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Client créé');
      }
      setShowClientModal(false);
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };
  
  const confirmDeleteClient = (client) => {
    setDeleteTarget({ type: 'client', item: client });
    setShowDeleteDialog(true);
  };

  // Document handlers (factures & devis)
  const openNewDocument = (type) => {
    setDocumentType(type);
    setEditingDocument(null);
    setDocumentForm({
      client_id: clients.length > 0 ? clients[0].id : '',
      lignes: [{ description: '', quantite: 1, prix_unitaire: 0, taux_tva: facturationSettings?.assujetti_tva ? (facturationSettings?.taux_tva_defaut || 20) : 0 }],
      conditions_paiement: facturationSettings?.conditions_paiement_defaut || 'Paiement à 30 jours',
      validite_jours: 30,
      notes: ''
    });
    setShowDocumentModal(true);
  };
  
  const openEditDocument = (doc, type) => {
    setDocumentType(type);
    setEditingDocument(doc);
    setDocumentForm({
      client_id: doc.client_id,
      lignes: doc.lignes.map((l) => ({
        description: l.description,
        quantite: l.quantite,
        prix_unitaire: l.prix_unitaire,
        taux_tva: l.taux_tva || 0
      })),
      conditions_paiement: doc.conditions_paiement || '',
      validite_jours: doc.validite_jours || 30,
      notes: doc.notes || ''
    });
    setShowDocumentModal(true);
  };
  
  const handleLigneChange = (index, newLigne) => {
    const newLignes = [...documentForm.lignes];
    newLignes[index] = newLigne;
    setDocumentForm({ ...documentForm, lignes: newLignes });
  };
  
  const handleAddLigne = () => {
    const defaultTva = facturationSettings?.assujetti_tva ? (facturationSettings?.taux_tva_defaut || 20) : 0;
    setDocumentForm({
      ...documentForm,
      lignes: [...documentForm.lignes, { description: '', quantite: 1, prix_unitaire: 0, taux_tva: defaultTva }]
    });
  };
  
  const handleRemoveLigne = (index) => {
    if (documentForm.lignes.length <= 1) {
      toast.error('Au moins une ligne est requise');
      return;
    }
    const newLignes = documentForm.lignes.filter((_, i) => i !== index);
    setDocumentForm({ ...documentForm, lignes: newLignes });
  };
  
  const handleSaveDocument = async () => {
    if (!documentForm.client_id) {
      toast.error('Veuillez sélectionner un client');
      return;
    }
    
    const hasValidLigne = documentForm.lignes.some((l) => l.description && l.quantite > 0 && l.prix_unitaire > 0);
    
    if (!hasValidLigne) {
      toast.error('Ajoutez au moins une ligne valide');
      return;
    }
    
    setSaving(true);
    try {
      const endpoint = documentType === 'facture' ? '/api/facturation/factures' : '/api/facturation/devis';
      const data = {
        client_id: documentForm.client_id,
        lignes: documentForm.lignes.filter((l) => l.description && l.prix_unitaire > 0),
        conditions_paiement: documentForm.conditions_paiement,
        notes: documentForm.notes
      };
      
      if (documentType === 'devis') {
        data.validite_jours = documentForm.validite_jours;
      }
      
      if (editingDocument) {
        await axios.put(
          `${API_URL}${endpoint}/${editingDocument.id}`,
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(`${documentType === 'facture' ? 'Facture' : 'Devis'} mis à jour`);
      } else {
        await axios.post(
          `${API_URL}${endpoint}`,
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(`${documentType === 'facture' ? 'Facture' : 'Devis'} créé`);
      }
      
      setShowDocumentModal(false);
      if (documentType === 'facture') {
        fetchFactures();
      } else {
        fetchDevis();
      }
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };
  
  const confirmDeleteDocument = (doc, type) => {
    setDeleteTarget({ type: type, item: doc });
    setShowDeleteDialog(true);
  };

  const handleDownloadPDF = async (doc, type) => {
    try {
      // Récupérer les infos entreprise et logo
      const settingsRes = await axios.get(`${API_URL}/api/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const entreprise = settingsRes.data?.business || {};
      const logoUrl = entreprise.logo_url ? `${API_URL}${entreprise.logo_url}` : null;
      
      // Récupérer les infos client
      const client = doc.client || {};
      
      // Générer le PDF
      const fileName = await exportFacturePDF(doc, type, entreprise, client, logoUrl, false);
      toast.success(`PDF téléchargé : ${fileName}`);
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Erreur lors de l\'export PDF');
    }
  };
  
  const handleDelete = async () => {
    if (!deleteTarget) return;
    
    setSaving(true);
    try {
      let endpoint = '';
      if (deleteTarget.type === 'client') {
        endpoint = `/api/facturation/clients/${deleteTarget.item.id}`;
      } else if (deleteTarget.type === 'facture') {
        endpoint = `/api/facturation/factures/${deleteTarget.item.id}`;
      } else if (deleteTarget.type === 'devis') {
        endpoint = `/api/facturation/devis/${deleteTarget.item.id}`;
      }
      
      await axios.delete(`${API_URL}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Supprimé avec succès');
      setShowDeleteDialog(false);
      setDeleteTarget(null);
      
      if (deleteTarget.type === 'client') fetchClients();
      if (deleteTarget.type === 'facture') { fetchFactures(); fetchStats(); }
      if (deleteTarget.type === 'devis') { fetchDevis(); fetchStats(); }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setSaving(false);
    }
  };
  
  // Statut handlers
  const updateStatut = async (doc, type, newStatut) => {
    try {
      const endpoint = type === 'facture' 
        ? `/api/facturation/factures/${doc.id}/statut`
        : `/api/facturation/devis/${doc.id}/statut`;
      
      if (type === 'facture') {
        await axios.put(`${API_URL}${endpoint}`, { statut: newStatut }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.put(`${API_URL}${endpoint}?statut=${newStatut}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      toast.success('Statut mis à jour');
      if (type === 'facture') { fetchFactures(); fetchStats(); }
      else { fetchDevis(); fetchStats(); }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };
  
  // Convert devis to facture
  const openConvertDialog = (devisItem) => {
    setConvertDevis(devisItem);
    setShowConvertDialog(true);
  };
  
  const handleConvertDevis = async () => {
    if (!convertDevis) return;
    
    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/api/facturation/factures/from-devis`,
        { devis_id: convertDevis.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Devis converti en facture');
      setShowConvertDialog(false);
      setConvertDevis(null);
      fetchDevis();
      fetchFactures();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la conversion');
    } finally {
      setSaving(false);
    }
  };

  // Calculate totals for document form
  const totalHT = documentForm.lignes.reduce((sum, l) => sum + (l.quantite || 0) * (l.prix_unitaire || 0), 0);
  const totalTVA = documentForm.lignes.reduce((sum, l) => {
    const ht = (l.quantite || 0) * (l.prix_unitaire || 0);
    return sum + ht * ((l.taux_tva || 0) / 100);
  }, 0);
  const totalTTC = totalHT + totalTVA;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="facturation-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Facturation</h1>
          <p className="text-slate-500 mt-1">Gérez vos devis, factures et clients</p>
        </div>
      </div>

      {/* KPIs */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpis-section">
          <KPICard
            title="CA Facturé"
            value={formatMontant(stats.ca_facture || 0)}
            subtitle={`${stats.nb_factures} factures`}
            icon={Euro}
            color="bg-blue-100 text-blue-600"
          />
          <KPICard
            title="CA Encaissé"
            value={formatMontant(stats.ca_encaisse || 0)}
            subtitle={`${stats.nb_payees} payées`}
            icon={CheckCircle2}
            color="bg-emerald-100 text-emerald-600"
          />
          <KPICard
            title="En attente"
            value={formatMontant(stats.ca_en_attente || 0)}
            subtitle={`${stats.nb_envoyees} envoyées`}
            icon={Clock}
            color="bg-amber-100 text-amber-600"
          />
          <KPICard
            title="Taux conversion"
            value={`${stats.taux_conversion}%`}
            subtitle={`${stats.nb_devis_convertis}/${stats.nb_devis} devis`}
            icon={TrendingUp}
            color="bg-purple-100 text-purple-600"
          />
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="factures" data-testid="tab-factures">
            <Receipt className="h-4 w-4 mr-2" />
            Factures ({factures.length})
          </TabsTrigger>
          <TabsTrigger value="devis" data-testid="tab-devis">
            <FileText className="h-4 w-4 mr-2" />
            Devis ({devis.length})
          </TabsTrigger>
          <TabsTrigger value="clients" data-testid="tab-clients">
            <Users className="h-4 w-4 mr-2" />
            Clients ({clients.length})
          </TabsTrigger>
        </TabsList>

        {/* TAB: Factures */}
        <TabsContent value="factures" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">Mes factures</h2>
            <Button onClick={() => openNewDocument('facture')} disabled={clients.length === 0} data-testid="btn-new-facture">
              <Plus className="h-4 w-4 mr-2" />
              Nouvelle facture
            </Button>
          </div>
          
          {clients.length === 0 && (
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="p-4 text-amber-800">
                <p>Créez d&apos;abord un client dans l&apos;onglet &quot;Clients&quot; pour pouvoir créer des factures.</p>
              </CardContent>
            </Card>
          )}
          
          {factures.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center text-slate-500">
                <Receipt className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Aucune facture pour le moment</p>
                <p className="text-sm mt-2">Créez votre première facture pour commencer</p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-0">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-slate-50">
                      <th className="text-left py-3 px-4 font-medium text-slate-600">N°</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Client</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Date</th>
                      <th className="text-right py-3 px-4 font-medium text-slate-600">Montant TTC</th>
                      <th className="text-center py-3 px-4 font-medium text-slate-600">Statut</th>
                      <th className="text-right py-3 px-4 font-medium text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {factures.map((facture) => (
                      <tr key={facture.id} className="border-b hover:bg-slate-50">
                        <td className="py-3 px-4 font-mono text-sm">{facture.numero}</td>
                        <td className="py-3 px-4">{facture.client?.nom || 'N/A'}</td>
                        <td className="py-3 px-4 text-slate-600">{facture.date_facture}</td>
                        <td className="py-3 px-4 text-right font-mono font-medium">{formatMontant(facture.total_ttc)}</td>
                        <td className="py-3 px-4 text-center">
                          <StatutBadge statut={facture.statut} type="facture" />
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="sm" onClick={() => handleDownloadPDF(facture, 'facture')} title="Télécharger PDF">
                              <Download className="h-4 w-4 text-slate-500" />
                            </Button>
                            {facture.statut === 'brouillon' && (
                              <>
                                <Button variant="ghost" size="sm" onClick={() => openEditDocument(facture, 'facture')}>
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => updateStatut(facture, 'facture', 'envoyee')}>
                                  <Send className="h-4 w-4 text-blue-500" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => confirmDeleteDocument(facture, 'facture')}>
                                  <Trash2 className="h-4 w-4 text-red-500" />
                                </Button>
                              </>
                            )}
                            {facture.statut === 'envoyee' && (
                              <Button variant="ghost" size="sm" onClick={() => updateStatut(facture, 'facture', 'payee')}>
                                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* TAB: Devis */}
        <TabsContent value="devis" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">Mes devis</h2>
            <Button onClick={() => openNewDocument('devis')} disabled={clients.length === 0} data-testid="btn-new-devis">
              <Plus className="h-4 w-4 mr-2" />
              Nouveau devis
            </Button>
          </div>
          
          {devis.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center text-slate-500">
                <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Aucun devis pour le moment</p>
                <p className="text-sm mt-2">Créez votre premier devis pour commencer</p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-0">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-slate-50">
                      <th className="text-left py-3 px-4 font-medium text-slate-600">N°</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Client</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Validité</th>
                      <th className="text-right py-3 px-4 font-medium text-slate-600">Montant TTC</th>
                      <th className="text-center py-3 px-4 font-medium text-slate-600">Statut</th>
                      <th className="text-right py-3 px-4 font-medium text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {devis.map((devisItem) => (
                      <tr key={devisItem.id} className="border-b hover:bg-slate-50">
                        <td className="py-3 px-4 font-mono text-sm">{devisItem.numero}</td>
                        <td className="py-3 px-4">{devisItem.client?.nom || 'N/A'}</td>
                        <td className="py-3 px-4 text-slate-600">{devisItem.date_validite}</td>
                        <td className="py-3 px-4 text-right font-mono font-medium">{formatMontant(devisItem.total_ttc)}</td>
                        <td className="py-3 px-4 text-center">
                          <StatutBadge statut={devisItem.statut} type="devis" />
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="sm" onClick={() => handleDownloadPDF(devisItem, 'devis')} title="Télécharger PDF">
                              <Download className="h-4 w-4 text-slate-500" />
                            </Button>
                            {devisItem.statut === 'brouillon' && (
                              <>
                                <Button variant="ghost" size="sm" onClick={() => openEditDocument(devisItem, 'devis')}>
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => updateStatut(devisItem, 'devis', 'envoye')}>
                                  <Send className="h-4 w-4 text-blue-500" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => confirmDeleteDocument(devisItem, 'devis')}>
                                  <Trash2 className="h-4 w-4 text-red-500" />
                                </Button>
                              </>
                            )}
                            {(devisItem.statut === 'envoye' || devisItem.statut === 'accepte') && devisItem.statut !== 'converti' && (
                              <Button variant="ghost" size="sm" onClick={() => openConvertDialog(devisItem)} title="Convertir en facture">
                                <ArrowRight className="h-4 w-4 text-purple-500" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* TAB: Clients */}
        <TabsContent value="clients" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">Mes clients</h2>
            <Button onClick={openNewClient} data-testid="btn-new-client">
              <Plus className="h-4 w-4 mr-2" />
              Nouveau client
            </Button>
          </div>
          
          {clients.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center text-slate-500">
                <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Aucun client pour le moment</p>
                <p className="text-sm mt-2">Ajoutez votre premier client pour créer des devis et factures</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {clients.map((client) => (
                <Card key={client.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-medium text-slate-900">{client.nom}</h3>
                        {client.email && <p className="text-sm text-slate-500">{client.email}</p>}
                        {client.ville && <p className="text-sm text-slate-400">{client.ville}</p>}
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEditClient(client)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => confirmDeleteClient(client)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Modal Client */}
      <Dialog open={showClientModal} onOpenChange={setShowClientModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingClient ? 'Modifier le client' : 'Nouveau client'}</DialogTitle>
            <DialogDescription>
              {editingClient ? 'Modifiez les informations du client' : 'Ajoutez un nouveau client à votre carnet'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nom / Raison sociale *</Label>
              <Input
                value={clientForm.nom}
                onChange={(e) => setClientForm({ ...clientForm, nom: e.target.value })}
                placeholder="Nom du client"
                data-testid="client-nom-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={clientForm.email}
                  onChange={(e) => setClientForm({ ...clientForm, email: e.target.value })}
                  placeholder="email@exemple.fr"
                  data-testid="client-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Téléphone</Label>
                <Input
                  value={clientForm.telephone}
                  onChange={(e) => setClientForm({ ...clientForm, telephone: e.target.value })}
                  placeholder="06 12 34 56 78"
                  data-testid="client-telephone-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Adresse</Label>
              <Input
                value={clientForm.adresse}
                onChange={(e) => setClientForm({ ...clientForm, adresse: e.target.value })}
                placeholder="123 rue de la Paix"
                data-testid="client-adresse-input"
              />
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Code postal</Label>
                <Input
                  value={clientForm.code_postal}
                  onChange={(e) => setClientForm({ ...clientForm, code_postal: e.target.value })}
                  placeholder="75001"
                  data-testid="client-code-postal-input"
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Ville</Label>
                <Input
                  value={clientForm.ville}
                  onChange={(e) => setClientForm({ ...clientForm, ville: e.target.value })}
                  placeholder="Paris"
                  data-testid="client-ville-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>SIRET (optionnel)</Label>
              <Input
                value={clientForm.siret}
                onChange={(e) => setClientForm({ ...clientForm, siret: e.target.value })}
                placeholder="123 456 789 00012"
                data-testid="client-siret-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClientModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleSaveClient} disabled={saving} data-testid="btn-save-client">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingClient ? 'Mettre à jour' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Document (Facture/Devis) */}
      <Dialog open={showDocumentModal} onOpenChange={setShowDocumentModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingDocument 
                ? (documentType === 'facture' ? 'Modifier la facture' : 'Modifier le devis')
                : (documentType === 'facture' ? 'Nouvelle facture' : 'Nouveau devis')
              }
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Client */}
            <div className="space-y-2">
              <Label>Client *</Label>
              <Select value={documentForm.client_id} onValueChange={(v) => setDocumentForm({ ...documentForm, client_id: v })}>
                <SelectTrigger data-testid="select-client">
                  <SelectValue placeholder="Sélectionnez un client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.nom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Lignes */}
            <div className="space-y-2">
              <Label>Lignes de {documentType}</Label>
              <div className="space-y-2">
                <div className="grid grid-cols-12 gap-2 text-xs font-medium text-slate-500 px-2">
                  <div className="col-span-4">Description</div>
                  <div className="col-span-2">Quantité</div>
                  <div className="col-span-2">Prix unit. HT</div>
                  {facturationSettings?.assujetti_tva && <div className="col-span-1">TVA</div>}
                  <div className={facturationSettings?.assujetti_tva ? 'col-span-2' : 'col-span-3'}>Total HT</div>
                  <div className="col-span-1"></div>
                </div>
                {documentForm.lignes.map((ligne, index) => (
                  <LigneForm
                    key={index}
                    ligne={ligne}
                    index={index}
                    onChange={handleLigneChange}
                    onRemove={handleRemoveLigne}
                    showTva={facturationSettings?.assujetti_tva}
                    prestations={prestations}
                  />
                ))}
                <Button variant="outline" onClick={handleAddLigne} className="w-full" data-testid="btn-add-ligne">
                  <Plus className="h-4 w-4 mr-2" />
                  Ajouter une ligne
                </Button>
              </div>
            </div>
            
            {/* Totaux */}
            <div className="bg-slate-50 p-4 rounded-lg space-y-2">
              <div className="flex justify-between">
                <span className="text-slate-600">Total HT</span>
                <span className="font-mono font-medium" data-testid="total-ht">{totalHT.toFixed(2)} €</span>
              </div>
              {facturationSettings?.assujetti_tva && (
                <div className="flex justify-between">
                  <span className="text-slate-600">TVA</span>
                  <span className="font-mono font-medium" data-testid="total-tva">{totalTVA.toFixed(2)} €</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-bold border-t pt-2">
                <span>Total TTC</span>
                <span className="font-mono" data-testid="total-ttc">{totalTTC.toFixed(2)} €</span>
              </div>
            </div>
            
            {/* Options */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Conditions de paiement</Label>
                <Input
                  value={documentForm.conditions_paiement}
                  onChange={(e) => setDocumentForm({ ...documentForm, conditions_paiement: e.target.value })}
                  placeholder="Paiement à 30 jours"
                  data-testid="conditions-paiement-input"
                />
              </div>
              {documentType === 'devis' && (
                <div className="space-y-2">
                  <Label>Validité (jours)</Label>
                  <Input
                    type="number"
                    value={documentForm.validite_jours}
                    onChange={(e) => setDocumentForm({ ...documentForm, validite_jours: parseInt(e.target.value) || 30 })}
                    min="1"
                    data-testid="validite-jours-input"
                  />
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <Label>Notes</Label>
              <Input
                value={documentForm.notes}
                onChange={(e) => setDocumentForm({ ...documentForm, notes: e.target.value })}
                placeholder="Notes ou mentions particulières"
                data-testid="notes-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDocumentModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleSaveDocument} disabled={saving} data-testid="btn-save-document">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingDocument ? 'Mettre à jour' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.type === 'client' && 'Êtes-vous sûr de vouloir supprimer ce client ? Cette action est irréversible.'}
              {deleteTarget?.type === 'facture' && 'Êtes-vous sûr de vouloir supprimer cette facture ?'}
              {deleteTarget?.type === 'devis' && 'Êtes-vous sûr de vouloir supprimer ce devis ?'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="btn-confirm-delete">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Convert Dialog */}
      <AlertDialog open={showConvertDialog} onOpenChange={setShowConvertDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Convertir en facture</AlertDialogTitle>
            <AlertDialogDescription>
              Voulez-vous convertir le devis {convertDevis?.numero} en facture ? 
              Une nouvelle facture sera créée avec les mêmes informations.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleConvertDevis} className="bg-purple-600 hover:bg-purple-700" data-testid="btn-confirm-convert">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Convertir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default FacturationPage;
